<template>
  <tr
    class="bg-white hover:bg-gray-50 transition-colors duration-150 ease-in-out text-right"
    @click="open = !open"
  >
    <td class="flex pl-3 py-5 items-center space-x-2 text-left">
      <button
        data-test="open-claim"
        type="button"
        class="btn-icon btn-icon-default"
        :aria-label="$t('ClaimCertifiedCosts.TradeRow.accessibility.openClaim')"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 transition-all duration-200"
          :class="[{'rotate-90': open}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
      </button>

      <input
        v-if="totalCosts.budget > 0 && ($props.claim.status === ClaimStatus.draft_pqs || $props.claim.status === ClaimStatus.draft_contractor)"
        v-model="selectTrade"
        type="checkbox"
        name="select_trade[]"
        class="form-checkbox"
        :aria-label="$t('ClaimCertifiedCosts.TradeRow.accessibility.selectTrade')"
        @click.stop=""
      >

      <span class="font-semibold text-gray-900">{{ trade.billNumber }}</span>

      <p class="w-60">
        {{ trade.name }}
      </p>
    </td>

    <td
      class="pr-3"
      :aria-label="$t('ClaimCertifiedCosts.TradeRow.accessibility.budgetAmount') + ': ' + australianCurrency(totalCosts.budget)"
    >
      <div v-if="totalCosts.budget !== 0">
        <span
          v-if="totalCosts.budget - totalCosts.adjustedBudget !== 0"
          class="text-gray-600"
        >Works:</span> {{ australianCurrency(totalCosts.budget) }}
      </div>

      <div v-if="totalCosts.budget - totalCosts.adjustedBudget !== 0">
        <span
          v-if="totalCosts.budget !== 0"
          class="text-gray-600"
        >Adjustments:</span> {{ australianCurrency(-(totalCosts.budget - totalCosts.adjustedBudget)) }}
      </div>
    </td>

    <td
      class="bg-green-200/50"
      :aria-label="$t('ClaimCertifiedCosts.TradeRow.accessibility.currentClaim') + ': ' + australianCurrency(totalCosts.currentClaim)"
    >
      {{ australianCurrency(totalCosts.currentClaim) }}
    </td>

    <td
      class="bg-green-200/50"
      :aria-label="$t('ClaimCertifiedCosts.TradeRow.accessibility.currentCertified') + ': ' + australianCurrency(totalCosts.currentCertified)"
    >
      {{ australianCurrency(totalCosts.currentCertified) }}
    </td>

    <td
      class="bg-green-200/50"
      :class="{ 'text-red-600': hasVariance && variance !== 0 }"
      :title="hasVariance && variance !== 0 ? varianceComment?.body : ''"
      :aria-label="$t('ClaimCertifiedCosts.TradeRow.accessibility.varianceAmount') + ': ' + australianCurrency(totalCosts.currentClaim - totalCosts.currentCertified)"
    >
      {{ australianCurrency(totalCosts.currentClaim - totalCosts.currentCertified) }}
    </td>

    <td
      class="bg-green-200/50"
      :aria-label="$t('ClaimCertifiedCosts.TradeRow.accessibility.previousAmount') + ': ' + australianCurrency(totalCosts.previous)"
    >
      {{ australianCurrency(totalCosts.previous) }}
    </td>

    <td
      class="bg-green-200/50 pr-3"
      :aria-label="$t('ClaimCertifiedCosts.TradeRow.accessibility.cumulativeAmount') + ': ' + australianCurrency(totalCosts.cumulative)"
    >
      {{ australianCurrency(totalCosts.cumulative) }}
    </td>

    <td
      class="bg-blue-100/50"
      :aria-label="$t('ClaimCertifiedCosts.TradeRow.accessibility.claimPercentage') + ': ' + (isNumber(totalCosts.currentClaim / totalCosts.budget) ? australianNumber((totalCosts.currentClaim / totalCosts.budget) * 100) : 0) + '%'"
    >
      <span v-if="showPercentages">
        {{ isNumber(totalCosts.currentClaim / totalCosts.budget) ? australianNumber((totalCosts.currentClaim / totalCosts.budget) * 100) : 0 }}%
      </span>
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
      :aria-label="$t('ClaimCertifiedCosts.TradeRow.accessibility.certifiedPercentage') + ': ' + (isNumber(totalCosts.currentCertified / totalCosts.budget) ? australianNumber((totalCosts.currentCertified / totalCosts.budget) * 100) : 0) + '%'"
    >
      {{ isNumber(totalCosts.currentCertified / totalCosts.budget) ? australianNumber((totalCosts.currentCertified / totalCosts.budget) * 100) : 0 }}%
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
      :aria-label="$t('ClaimCertifiedCosts.TradeRow.accessibility.previousPercentage') + ': ' + (isNumber(totalCosts.previous / totalCosts.budget) ? australianNumber((totalCosts.previous / totalCosts.budget) * 100) : 0) + '%'"
    >
      {{ isNumber(totalCosts.previous / totalCosts.budget) ? australianNumber((totalCosts.previous / totalCosts.budget) * 100) : 0 }}%
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50 pr-3"
      :aria-label="$t('ClaimCertifiedCosts.TradeRow.accessibility.cumulativePercentage') + ': ' + (isNumber(totalCosts.cumulative / totalCosts.budget) ? australianNumber((totalCosts.cumulative / totalCosts.budget) * 100) : 0) + '%'"
    >
      {{ isNumber(totalCosts.cumulative / totalCosts.budget) ? australianNumber((totalCosts.cumulative / totalCosts.budget) * 100) : 0 }}%
    </td>

    <td
      class="pr-3"
      :aria-label="$t('ClaimCertifiedCosts.TradeRow.accessibility.remainingBudget') + ': ' + australianCurrency(Math.abs(totalCosts.budget - totalCosts.cumulative))"
    >
      {{ australianCurrency(Math.abs(totalCosts.budget - totalCosts.cumulative)) }}
    </td>
  </tr>

  <template v-if="open">
    <TradeRowItem
      v-for="tradeItem in topLevelTradeItems"
      :key="tradeItem.id"
      :trade="$props.trade"
      :claim="$props.claim"
      :trade-item="tradeItem"
      :show-percentages="$props.showPercentages"
    />
  </template>
</template>

<script setup lang="ts">
import { useSignedData } from '@/composables/useSignedData';
import { australianCurrency, australianNumber, falsey, isNumber } from '@/helpers';
import Claim, { ClaimStatus } from '@/models/Claim';
import Trade from '@/models/Trade';
import TradeItem from '@/models/TradeItem';
import { useCommentsStore } from '@/store/comments';
import { useTradeItemsStore } from '@/store/tradeItems';
import { computed, ref, watch } from 'vue';
import TradeRowItem from './TradeRowItem.vue';

const props = defineProps<{
  trade: Trade;
  claim: Claim;
  showPercentages?: boolean;
}>();

const signedParams = useSignedData();
const tradeItemsStore = useTradeItemsStore();

const tradeItemIds = computed(() => {
  return (signedParams.models.tradeItems as TradeItem[]).map((tradeItem) => {
    return tradeItem.id;
  });
});

const topLevelTradeItems = computed(() => {
  return tradeItemsStore.models
    .where('trade_id', props.trade.id)
    .whereNull('parent_id')
    .with('trade_item_costs', (query) => {
      query.where('claim_id', props.claim.id);
    }).orderBy('sort_order')
    .get();
});

const allTradeItemsWithoutChildren = computed(() => {
  return tradeItemsStore.models
    .whereIn('id', tradeItemIds.value)
    .where('trade_id', props.trade.id)
    .where('has_children', false)
    .with('trade_item_costs', (query) => {
      query.where('claim_id', props.claim.id);
    }).orderBy('sort_order')
    .get();
});

const totalCosts = computed(() => {
  const totals = {
    budget: 0,
    adjustedBudget: 0,
    currentClaim: 0,
    currentCertified: 0,
    previous: 0,
    cumulative: 0,
  };

  allTradeItemsWithoutChildren.value.forEach((tradeItem) => {
    if(tradeItem.budget > 0) {
      totals.budget += tradeItem.budget;
    }

    totals.adjustedBudget += tradeItem.budget;

    totals.previous += tradeItem.previous(props.claim);
    const tradeItemCost = tradeItem.trade_item_costs[0];

    if(tradeItemCost) {
      totals.currentClaim += tradeItemCost.current_claim;
      totals.currentCertified += tradeItemCost.current_certified;
    }
  });

  totals.cumulative = totals.previous + totals.currentCertified;

  return totals;
});

const open = ref(false);
const selectTrade = ref(false);

watch(selectTrade, (newValue) => {
  if(newValue) {
    allTradeItemsWithoutChildren.value
      .filter((tradeItem) => tradeItem.budget > 0)
      .forEach((tradeItem) => {
        tradeItemsStore.selectedTradeItems.add(tradeItem.id);
      });
  } else {
    allTradeItemsWithoutChildren.value
      .filter((tradeItem) => tradeItem.budget > 0)
      .forEach((tradeItem) => {
        tradeItemsStore.selectedTradeItems.delete(tradeItem.id);
      });
  }
});

const commentsStore = useCommentsStore();

const varianceComment = computed(() => {
  return commentsStore.models.where('commentable_id', props.trade.id).where('commentable_type', props.trade.$entity())
    .where('creator_id', props.claim.id).where('creator_type', props.claim.$entity())
    .first();
});

const hasVariance = computed(() => {
  return !falsey(totalCosts.value?.currentClaim) && !falsey(totalCosts.value?.currentCertified);
});

const variance = computed(() => {
  return totalCosts.value?.currentClaim - totalCosts.value?.currentCertified;
});
</script>

<style scoped></style>
